import PropTypes from 'prop-types';
import { Component, cloneElement } from 'react';
import {findDOMNode} from 'react-dom';
import lodash from 'lodash';
import styles from './LazyLoadingScrollList.scss';

export class LazyLoadingScrollList extends Component {
    constructor(props, ...args) {
        super(props, ...args);
        let {scrollRatio} = props;
        this.state ={
            scrollRatio
        };

        this.scrollEvent = lodash.debounce(this.scrollEvent.bind(this), 40);
    }

    get scrollToTopThreshold() {
        return this.props.scrollToTopThreshold || 0.15;
    }

    get scrollToBottomThreshold() {
        return this.props.scrollToTopThreshold || 0.85;
    }

    scrollEvent() {
        if (!this.scrollEventTrigger()) {
            return;
        }
        this.onScrollStart(this.state.scrollRatio);
        const {scrollTop, scrollHeight, clientHeight} = this.scrollAreaNode;
        const scrollRatio = scrollTop / (scrollHeight - clientHeight);
        this.setState({
            scrollRatio
        });
        this.onScroll(scrollRatio);
        if (scrollRatio < this.scrollToTopThreshold)  {
            this.onScrollToTop();
        }
        if (scrollRatio > this.scrollToBottomThreshold) {
            this.onScrollToBottom();
        }
        this.onScrollEnd(scrollRatio);
    }

    callScrollEvent(scrollEventFunc, scrollRatio) {
        if (typeof scrollEventFunc === 'function') {
            const {data} = this.props;
            scrollEventFunc(scrollRatio, data);
        }
    }

    scrollEventTrigger() {
        if (typeof this.props.scrollEventTrigger === 'function') {
            return this.props.scrollEventTrigger(this.state.scrollRatio, this.props.data);
        }
        return true;
    }

    onScrollStart(previousScrollRatio) {
        this.callScrollEvent(this.props.onScrollStart, previousScrollRatio);
    }

    onScroll(scrollRatio) {
        this.callScrollEvent(this.props.onScroll, scrollRatio);
    }

    onScrollToTop(scrollRatio) {
        this.callScrollEvent(this.props.onScrollToTop, scrollRatio);
    }

    onScrollToBottom(scrollRatio) {
        this.callScrollEvent(this.props.onScrollToBottom, scrollRatio);
    }

    onScrollEnd(scrollRatio) {
        this.callScrollEvent(this.props.onScrollEnd, scrollRatio);
    }

    subscribeToScrollableArea(scrollableArea) {
        if (!scrollableArea || this.scrollArea) {
            return;
        }
        this.scrollEventHandler = this.scrollEvent.bind(this);
        this.scrollArea = scrollableArea;
        this.scrollAreaNode = findDOMNode(this.scrollArea);
        this.scrollAreaNode.addEventListener('scroll', this.scrollEventHandler);
    }

    unsubscribeFromScrollableArea() {
        if (this.scrollEventHandler) {
            this.scrollAreaNode.removeEventListener('scroll', this.scrollEventHandler);
            this.scrollEventHandler = null;
            this.scrollAreaNode = null;
            this.scrollArea = null;
        }
    }

    componentWillUnmount() {
        this.unsubscribeFromScrollableArea();
    }

    getClassName() {
        const {className} = this.props;
        return `${styles.scrollableArea} ${className}`.trim();
    }

    render() {
        const renderedData = this.renderData();
        const { loading } = this.props;
        return (
            <div className={styles.scrollableAreaContainer} data-cy="LazyLoadingScrollList-render">
                <ul ref={scrollableArea => this.subscribeToScrollableArea(scrollableArea)}
                    data-cy="LazyLoadingScrollList-ul"
                    className={this.getClassName()}>
                    {renderedData}
                </ul>
                <LoadingComponent loading={loading} />
            </div>

        );
    }

    renderData() {
        const {data, componentFactory} = this.props;
        return lodash.map(data, (dataItem, index) => {
            return cloneElement(componentFactory(dataItem, index, data), {
                key: index
            });
        });
    }
}

LazyLoadingScrollList.propTypes = {
    data: PropTypes.array,
    componentFactory: PropTypes.func.isRequired,
    scrollToTopThreshold: PropTypes.number,
    scrollToBottomThreshold: PropTypes.number,
    scrollEventTrigger: PropTypes.func, // signature(scrollRatio, data): bool
    onScrollStart: PropTypes.func, // signature(scrollRatio, data)
    onScroll: PropTypes.func, // signature(scrollRatio, data)
    onScrollToTop: PropTypes.func, // signature(scrollRatio, data)
    onScrollToBottom: PropTypes.func, // signature(scrollRatio, data)
    onScrollEnd: PropTypes.func, // signature(scrollRatio, data)
    throttleDuration: PropTypes.number, // in ms
    scrollRatio: PropTypes.number,
    className: PropTypes.string,
};

export class LoadingComponent extends Component {
    render() {
        const {loading} = this.props;
        if (!loading) {
            return null;
        }
        return (
            <div className={styles.loadingComponent} data-cy="LazyLoadingScrollList-LoadingComponent">Loading...</div>
        );
    }
}

export default LazyLoadingScrollList;
